import * as React from "react";
import { omit } from "ramda";
import { Selection, type IColumn, type IObjectWithKey } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { VehicleDeviceSortKey, VehicleDevice } from "../../api";
import {
    type VehicleDevicesApiResource,
    type VehicleDevicesQuery,
} from "../../domain/repositories/repository-vehicle-devices";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ListVehicleDeviceItemCell } from "../atoms/list-vehicle-device-item-cell";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SelectionState } from "../../utils/selection-state";
import { defaultPageSize } from "../../utils/constants";

export interface ListVehicleDevicesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListVehicleDeviceItem extends IObjectWithKey {
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListVehicleDevices extends React.Component<ListVehicleDevicesProps> {
    private _listStateId = createUuid();

    constructor(props: ListVehicleDevicesProps) {
        super(props);
        makeObservable(this);
    }
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    componentDidMount(): void {
        store.serviceListStates.vehicleDevices.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<VehicleDeviceSortKey> {
        return store.serviceListStates.vehicleDevices.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<VehicleDevicesApiResource> {
        return store.serviceListStates.vehicleDevices.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.vehicleDevices.getSelectionState(this.listStateId);
    }
    @computed private get query(): VehicleDevicesQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get vehicleDevices(): (VehicleDevice | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryVehicleDevices.byId(id));
        }
        return store.repositoryVehicleDevices.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListVehicleDeviceItem | undefined)[] {
        return this.vehicleDevices.map((vehicleDevice) => {
            if (!vehicleDevice) {
                return;
            }

            return {
                key: vehicleDevice.id,
            };
        });
    }

    private renderItem(deviceId: UUID, column: IColumn): JSX.Element {
        return <ListVehicleDeviceItemCell vehicleDeviceId={deviceId} column={column} />;
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [
            {
                name: store.i18n.t("listVehicleDevices.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 100,
            },
            {
                name: store.i18n.t("listVehicleDevices.column.vehicle.id"),
                key: "vehicleId",
                minWidth: 300,
            },
            {
                name: store.i18n.t("listVehicleDevices.column.createdAt.name"),
                key: "createdAt",
                minWidth: 150,
            },
            {
                name: store.i18n.t("listVehicleDevices.column.lastUpdated.name"),
                key: "firmwareLastUpdatedAt",
                minWidth: 150,
            },
            {
                name: store.i18n.t("listVehicleDevices.column.currentFirmwareVersion.name"),
                key: "currentFirmwareVersion",
                minWidth: 200,
            },
        ];

        return columns;
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.vehicleDevices.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    // Disable right-click to stay consistent with other lists
                    onItemContextMenu={() => {}}
                    selection={this.selection}
                    onRenderItemColumn={(item: ListVehicleDeviceItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
